import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, TextField, Tooltip } from '@mui/material';
import { VariableFontConfig } from '../utils/fontHelpers';
import './ReusableFontPicker.css';

export interface FontOption {
  value: string;
  label: string;
  familyName: string;
  variableFontConfig?: VariableFontConfig;
}

export interface FontFamilyOption {
  familyName: string;
  fonts: FontOption[];
}

interface ReusableFontPickerProps {
  value: string | undefined;
  fontFamilies: FontFamilyOption[];
  onChange: (nextFont: string) => void;
  className?: string;
}

const toCssFontFamily = (fontName: string | undefined): string | undefined => {
  if (!fontName) {
    return undefined;
  }
  return `${JSON.stringify(fontName)}, sans-serif`;
};

const ReusableFontPicker: React.FC<ReusableFontPickerProps> = ({
  value,
  fontFamilies,
  onChange,
  className = '',
}) => {
  const resolvedFontFamilies: FontFamilyOption[] = useMemo(() => {
    if (!value) return fontFamilies;
    const hasMatch: boolean = fontFamilies.some(
      (fontFamily: FontFamilyOption) => fontFamily.fonts.some((fontOption: FontOption) => fontOption.value === value)
    );
    if (hasMatch) return fontFamilies;
    return [
      {
        familyName: 'Current Font',
        fonts: [{ value, label: value, familyName: 'Current Font' }],
      },
      ...fontFamilies,
    ];
  }, [fontFamilies, value]);

  const selectedOption: FontOption | null = useMemo(() => {
    if (!value) return null;
    for (const fontFamily of resolvedFontFamilies) {
      const match: FontOption | undefined = fontFamily.fonts.find((fontOption: FontOption) => fontOption.value === value);
      if (match) return match;
    }
    return { value, label: value, familyName: 'Current Font' };
  }, [resolvedFontFamilies, value]);
  const [selectedFamilyName, setSelectedFamilyName] = useState<string>(
    selectedOption?.familyName ?? resolvedFontFamilies[0]?.familyName ?? ''
  );
  const previousValueRef = useRef<string | undefined>(value);

  useEffect(() => {
    const hasValueChanged: boolean = previousValueRef.current !== value;
    if (hasValueChanged) {
      previousValueRef.current = value;
      setSelectedFamilyName(selectedOption?.familyName ?? resolvedFontFamilies[0]?.familyName ?? '');
      return;
    }
    if (
      selectedFamilyName &&
      !resolvedFontFamilies.some((fontFamily: FontFamilyOption) => fontFamily.familyName === selectedFamilyName)
    ) {
      setSelectedFamilyName(resolvedFontFamilies[0]?.familyName ?? '');
    }
  }, [resolvedFontFamilies, selectedFamilyName, selectedOption?.familyName, value]);

  const selectedFamily: FontFamilyOption | null =
    resolvedFontFamilies.find((fontFamily: FontFamilyOption) => fontFamily.familyName === selectedFamilyName) ??
    resolvedFontFamilies[0] ??
    null;
  const selectedFamilyPreviewOption: FontOption | undefined = selectedFamily?.fonts[0];
  const visibleFontOptions: FontOption[] = selectedFamily?.fonts ?? [];
  const visibleSelectedOption: FontOption | null = selectedOption && selectedFamily?.familyName === selectedOption.familyName
    ? selectedOption
    : null;

  return (
    <div className={`font-picker-pair ${className}`.trim()}>
      <Autocomplete<FontFamilyOption, false, false, false>
        className="font-picker-autocomplete font-family-picker"
        fullWidth
        disablePortal
        options={resolvedFontFamilies}
        value={selectedFamily}
        isOptionEqualToValue={(option: FontFamilyOption, optionValue: FontFamilyOption) =>
          option.familyName === optionValue.familyName
        }
        getOptionLabel={(option: FontFamilyOption) => option.familyName}
        onChange={(_, nextFamily: FontFamilyOption | null) => {
          if (nextFamily) {
            setSelectedFamilyName(nextFamily.familyName);
          }
        }}
        renderOption={(props, option) => (
          <li {...props} style={{ ...props.style, fontFamily: toCssFontFamily(option.fonts[0]?.value) }}>
            {option.familyName}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            placeholder="Family"
            InputProps={{
              ...params.InputProps,
              sx: {
                overflow: 'hidden',
                '& .MuiAutocomplete-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: toCssFontFamily(selectedFamilyPreviewOption?.value),
                },
              },
            }}
          />
        )}
      />
      <Tooltip
        title={visibleSelectedOption?.label ?? ''}
        placement="top"
        arrow
        disableHoverListener={!visibleSelectedOption?.label}
      >
        <Autocomplete<FontOption, false, false, false>
          className="font-picker-autocomplete font-file-picker"
          fullWidth
          disablePortal
          options={visibleFontOptions}
          value={visibleSelectedOption}
          isOptionEqualToValue={(option: FontOption, optionValue: FontOption) =>
            option.value === optionValue.value
          }
          getOptionLabel={(option: FontOption) => option.label}
          onChange={(_, nextOption: FontOption | null) => {
            if (nextOption) {
              onChange(nextOption.value);
            }
          }}
          renderOption={(props, option) => (
            <li {...props} style={{ ...props.style, fontFamily: toCssFontFamily(option.value) }}>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              variant="outlined"
              placeholder="Font"
              InputProps={{
                ...params.InputProps,
                sx: {
                  overflow: 'hidden',
                  '& .MuiAutocomplete-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: toCssFontFamily(visibleSelectedOption?.value),
                  },
                },
              }}
            />
          )}
        />
      </Tooltip>
    </div>
  );
};

export default ReusableFontPicker;
